// src/components/tactic/TacticReviewPanel.jsx
// Ask the coach AI to read the plan back. The geometry and the sight-lines are
// resolved before the request leaves the browser (see planSummary.js), so what
// comes back is judgement on real numbers rather than a guess at them.

import React, { useState } from "react";
import { Btn, Label, Panel, T, TextInput } from "./tacticUi";

const Section = ({ title, children }) => (
  <div style={{ marginTop: 12 }}>
    <Label>{title}</Label>
    {children}
  </div>
);

const Bullets = ({ items, color }) => (
  <ul style={{ margin: 0, paddingLeft: 17, display: "grid", gap: 5 }}>
    {items.map((line, i) => (
      <li key={i} style={{ fontSize: 12.5, lineHeight: 1.6, color: color || T.ink }}>
        {line}
      </li>
    ))}
  </ul>
);

export default function TacticReviewPanel({ review, busy, error, tokenCount, onReview, onClear }) {
  const [question, setQuestion] = useState("");
  const empty = !tokenCount;

  return (
    <Panel
      title="ให้ AI ตรวจแผน"
      right={
        review ? (
          <Btn tone="ghost" onClick={onClear} style={{ padding: "3px 8px", fontSize: 11 }}>
            ล้าง
          </Btn>
        ) : null
      }
    >
      {empty ? (
        <div style={{ color: T.faint, fontSize: 12, lineHeight: 1.7 }}>
          วางฮีโร่ลงกระดานก่อน แล้ว AI จะอ่านตำแหน่ง เส้นทาง และการมองเห็นในแต่ละจังหวะให้
        </div>
      ) : (
        <>
          <TextInput
            placeholder="อยากถามอะไรเป็นพิเศษไหม (เว้นว่างได้)"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) onReview(question.trim());
            }}
            style={{ marginBottom: 8 }}
          />
          <Btn
            tone="accent"
            onClick={() => onReview(question.trim())}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {busy ? "กำลังอ่านแผน…" : "ตรวจแผนนี้"}
          </Btn>

          <div style={{ marginTop: 8, fontSize: 10.5, color: T.faint, lineHeight: 1.6 }}>
            อ่านจากป้ายจังหวะที่ตั้งไว้ — ถ้ายังไม่ได้ตั้ง จะอ่านที่ต้น กลาง ท้ายคลิป
          </div>
        </>
      )}

      {error && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "#ff8ba3",
            lineHeight: 1.6,
            background: "rgba(255,77,109,0.08)",
            border: "1px solid rgba(255,77,109,0.3)",
            borderRadius: 8,
            padding: "8px 10px",
          }}
        >
          {error}
        </div>
      )}

      {review && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>{review.summary}</div>

          {review.strengths.length > 0 && (
            <Section title="จุดที่ดี">
              <Bullets items={review.strengths} color={T.dim} />
            </Section>
          )}

          {review.risks.length > 0 && (
            <Section title="จุดเสี่ยง">
              <div style={{ display: "grid", gap: 8 }}>
                {review.risks.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      background: "rgba(255,16,38,0.07)",
                      border: "1px solid rgba(255,16,38,0.25)",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ fontSize: 10.5, color: T.accent, marginBottom: 3 }}>
                      {r.moment}
                    </div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>{r.issue}</div>
                    <div style={{ fontSize: 12, lineHeight: 1.6, color: T.dim, marginTop: 4 }}>
                      → {r.fix}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {review.checks.length > 0 && (
            <Section title="เช็คก่อนเข้า">
              <Bullets items={review.checks} color={T.dim} />
            </Section>
          )}

          <div style={{ marginTop: 10, fontSize: 10, color: T.faint }}>
            ตรวจเมื่อ {review.at} · ตัวเลขทั้งหมดคำนวณโดยแอป ไม่ใช่โดยโมเดล
          </div>
        </div>
      )}
    </Panel>
  );
}
